<!-- components/sidebar/AppSidebar.vue - Fixed sidebar with proper navigation filtering -->
<template>
  <VaSidebar v-model="writableVisible" class="app-sidebar" :width="sidebarWidth" :color="color" gradient>
    <!-- User Info Section -->
    <div class="pa-3">
      <div class="user-info-section pa-3">
        <div class="flex items-center gap-3">
          <VaAvatar color="primary" size="medium">
            {{ getUserInitials }}
          </VaAvatar>
          <div class="flex-1">
            <div class="font-semibold text-sm">{{ userName }}</div>
            <div class="text-xs opacity-70">{{ userRole }}</div>
          </div>
        </div>
      </div>
    </div>

    <VaDivider />

    <!-- Navigation Menu -->
    <div class="navigation-menu">
      <template v-for="(route, idx) in mainRoutes" :key="`route-${idx}`">
        <!-- Routes without children - Simple navigation item -->
        <VaSidebarItem
          v-if="!route.children || route.children.length === 0"
          :active="isActiveRoute(route)"
          :active-color="activeColor"
          :text-color="textColor(route)"
          :aria-label="`Navigate to ${t(route.displayName)}`"
          hover-opacity="0.10"
          class="sidebar-item parent-item standalone"
          @click="handleRouteClick(route)"
        >
          <VaSidebarItemContent class="py-3 px-3">
            <VaIcon v-if="route.meta.icon" :name="route.meta.icon" :color="iconColor(route)" size="20px" class="mr-3" />
            <VaSidebarItemTitle class="leading-6">
              {{ t(route.displayName) }}
            </VaSidebarItemTitle>
          </VaSidebarItemContent>
        </VaSidebarItem>

        <!-- Routes with children - Expandable parent with children -->
        <div v-else class="parent-with-children">
          <!-- Parent Item (Clickable to expand/collapse) -->
          <VaSidebarItem
            class="sidebar-item parent-item has-children"
            :active="isParentActive(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            hover-opacity="0.10"
            @click="toggleExpanded(idx)"
          >
            <VaSidebarItemContent class="py-3 px-3">
              <VaIcon
                v-if="route.meta.icon"
                :name="route.meta.icon"
                :color="iconColor(route)"
                size="20px"
                class="mr-3"
              />
              <VaSidebarItemTitle class="leading-6 flex-1">
                {{ t(route.displayName) }}
              </VaSidebarItemTitle>
              <VaIcon
                :name="expandedStates[idx] ? 'expand_less' : 'expand_more'"
                size="20px"
                :color="iconColor(route)"
                class="expand-icon"
                :class="{ 'rotate-180': expandedStates[idx] }"
              />
            </VaSidebarItemContent>
          </VaSidebarItem>

          <!-- Child routes (Collapsible) -->
          <Transition name="slide">
            <div v-show="expandedStates[idx]" class="child-routes">
              <VaSidebarItem
                v-for="(childRoute, childIdx) in route.children"
                :key="`child-${idx}-${childIdx}`"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Navigate to ${t(childRoute.displayName)}`"
                hover-opacity="0.10"
                class="sidebar-item child-item"
                @click.stop="handleRouteClick(childRoute)"
              >
                <VaSidebarItemContent class="py-2 px-3">
                  <VaIcon
                    v-if="childRoute.meta?.icon"
                    :name="childRoute.meta.icon"
                    :color="isActiveChildRoute(childRoute) ? 'primary' : 'secondary'"
                    size="16px"
                    class="mr-2 ml-8"
                  />
                  <span v-else class="ml-10"></span>
                  <VaSidebarItemTitle class="leading-5 text-sm">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </Transition>
        </div>
      </template>
      <!-- DIVIDER BEFORE BOTTOM ROUTES -->
      <VaDivider
        v-if="bottomRoutes.length"
        class="my-2 mx-3"
      />

      <!-- BOTTOM ROUTES -->
      <template v-for="(route, idx) in bottomRoutes" :key="`bottom-${idx}`">
        <VaSidebarItem
          :active="isActiveRoute(route)"
          :active-color="activeColor"
          :text-color="textColor(route)"
          hover-opacity="0.10"
          class="sidebar-item parent-item standalone bottom-route"
          @click="handleRouteClick(route)"
        >
          <VaSidebarItemContent class="py-3 px-3">
            <VaIcon
              v-if="route.meta.icon"
              :name="route.meta.icon"
              :color="iconColor(route)"
              size="20px"
              class="mr-3"
            />
            <VaSidebarItemTitle>
              {{ route.displayName }}
            </VaSidebarItemTitle>
          </VaSidebarItemContent>
        </VaSidebarItem>
      </template>
    </div>

    <!-- Bottom Section -->
    <template #bottom>
      <VaDivider />
      <div class="pa-3">
        <div v-if="userStore.user?.entityLookUpList && userStore.user.entityLookUpList.length > 1" class="mb-3">
          <VaSelect
            v-model="selectedEntityId"
            :options="entityOptions"
            label="Entity"
            text-by="text"
            value-by="value"
            class="w-full"
            size="small"
            @update:modelValue="handleEntityChange"
          />
        </div>
        <VaButton preset="secondary" size="small" class="w-full" @click="logout">
          <VaIcon name="logout" size="16px" class="mr-1" />
          Logout
        </VaButton>
      </div>
    </template>
  </VaSidebar>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useMsal } from 'vue3-msal-plugin'
import { useUserStore } from '../../stores/user'
import { useSidebarStore } from '../../stores/sidebar-store'
import { storeToRefs } from 'pinia'

// Navigation route interface
interface INavigationRoute {
  name: string
  displayName: string
  meta: {
    icon?: string
    permissions?: string[]
    requiresAuth?: boolean
    isPowerBI?: boolean
    defaultCodename?: string
    defaultName?: string
    pageName?: string
  }
  children?: INavigationRoute[]
}

// Props and Emits
const props = defineProps<{
  visible?: boolean
  mobile?: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

// Composables
const { getColor, colorToRgba } = useColors()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { instance } = useMsal()
const userStore = useUserStore()
const sidebarStore = useSidebarStore()
const { isAuthenticated } = storeToRefs(userStore)

// State
const expandedStates = reactive<Record<number, boolean>>({})
const selectedEntityId = ref<number>(userStore.user?.lastSelectedEntity?.id || 0)

// Computed properties
const writableVisible = computed({
  get: () => props.visible ?? true,
  set: (v: boolean) => emit('update:visible', v),
})

const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
const color = computed(() => getColor('background-secondary'))
const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

// User info
const getUserInitials = computed(() => {
  const name = userStore.displayName || 'User'
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
})

const userName = computed(() => userStore.displayName || 'User')
const userRole = computed(() => userStore.userRoleDisplay || 'Role')

// Entity options for dropdown
const entityOptions = computed(() => {
  const entities = userStore.user?.entityLookUpList || []
  return entities.map((entity) => ({
    text: entity.entity_name,
    value: entity.id,
  }))
})

// Static navigation route definitions (parent-level)
// PowerBI children are fetched dynamically from the sidebar store
const staticNavigationRoutes: INavigationRoute[] = [
  {
    name: 'dashboard',
    displayName: 'menu.dashboard',
    meta: {
      icon: 'dashboard',
      permissions: ['view_all', 'view_user', 'view_hr', 'view_leader', 'view_admin'], //check this properly
      requiresAuth: true,
    },
  },
  {
    name: 'user-dashboard',
    displayName: 'menu.bireports', //Called as 'User'
    meta: {
      icon: 'folder_shared',
      permissions: ['view_user'],
      requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'user',
      defaultName: 'user-dashboard',
    },
  },
  {
    name: 'leads-dashboard',
    displayName: 'menu.leadsreports', //Called as 'People Leader'
    meta: {
      icon: 'group_work',
      permissions: ['view_leader'],
      requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'leads',
      defaultName: 'Leads-Dashboard',
    },
  },
  {
    name: 'leads-admin-dashboard',
    displayName: 'menu.leadsadminreports', //Called as 'People Leader Insights'
    meta: {
      icon: 'group_work',
      permissions: ['view_leader_admin'],
      requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'leaderadmin',
      defaultName: 'Leads-Admin-Dashboard',
    },
  },
  {
    name: 'hr-dashboard',
    displayName: 'menu.execheadreports', //Called as 'HR'
    meta: {
      icon: 'leaderboard',
      permissions: ['view_hr'],
      requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'hr',
      defaultName: 'HR-Dashboard',
    },
  },
  {
    name: 'hr-admin-dashboard',
    displayName: 'menu.execheadadminreports', //Called as 'HR Admin'
    meta: {
      icon: 'leaderboard',
      permissions: ['view_hr_admin'],
      requiresAuth: true,
      isPowerBI: true,
      defaultCodename: 'hradmin',
      defaultName: 'HR-Admin-Dashboard',
    },
  },
  {
    name: 'admin',
    displayName: 'menu.admin', //leads reports
    meta: {
      icon: 'settings',
      permissions: ['view_admin'], //Uncomment this line to provide permission to the user with view_admin permission later.
      requiresAuth: true,
    },
    children: [
      {
        meta: {
          icon: 'folder_shared',
        },
        name: 'user-admin',
        displayName: 'Users'
      },
      {
        meta: {
          icon: 'folder_shared',
        },
        name: 'group-admin',
        displayName: 'Groups'
      },
      {
        meta: {
          icon: 'folder_shared',
        },
        name: 'permission-admin',
        displayName: 'Permissions'
      },
      {
        meta: {
          icon: 'folder_shared',
        },
        name: 'super-admin',
        displayName: 'Manage'
      },
      {
        meta: {
          icon: 'view_sidebar',
        },
        name: 'report-sidebar-admin',
        displayName: 'Sidebar Pages'
      },
      {
        meta: {
          icon: 'assessment',
        },
        name: 'report-admin',
        displayName: 'Reports'
      },
      {
        meta: {
          icon: 'category',
        },
        name: 'report-category-admin',
        displayName: 'Report Categories'
      }
    ]
  },
  {
    name: 'preferences',
    displayName: 'Preferences',
    meta: {
      icon: 'settings',
      requiresAuth: true,
    },
  }
]

// Computed navigation routes: merges static parent definitions with dynamic children from API
const navigationRoutes = computed<INavigationRoute[]>(() => {
  return staticNavigationRoutes.map(route => {
    // For PowerBI routes, dynamically inject children from sidebar store
    if (route.meta.isPowerBI && route.meta.defaultCodename) {
      const dynamicChildren = sidebarStore.getChildrenForCodename(route.meta.defaultCodename)
      if (dynamicChildren.length > 0) {
        return { ...route, children: dynamicChildren as INavigationRoute[] }
      }
      // No dynamic children available — show parent without children (navigates to report)
      return route
    }
    // Non-PowerBI routes (admin, dashboard, etc.) keep their static children
    return route
  })
})

// Filter routes based on permissions
const filteredRoutes = computed(() => {
  const isAuthenticated = userStore.isAuthenticated
  const isSuperUser = userStore.user?.isSuperUser || false

  return navigationRoutes.value
    .map((route) => {
      // Always show public routes (requiresAuth: false)
      if (!route.meta.requiresAuth) {
        return route
      }

      // Check authentication for protected routes
      if (!isAuthenticated) {
        return null
      }

      // TEMPORARY: Only show user-dashboard menu (view_user permission)
      // Comment out to restore normal behavior
      // if (route.name !== 'user-dashboard' && route.name !== 'dashboard' && route.name !== 'preferences') {
      //   return null
      // }

      // Super users see everything (commented out for testing)
       if (isSuperUser) {
         return route
       }

      // Check permissions for the parent route
      if (route.meta.permissions && route.meta.permissions.length > 0) {
        const hasPermission = userStore.hasAnyPermission(route.meta.permissions)
        if (!hasPermission) {
          return null
        }
      }

      // Filter children based on permissions
      if (route.children) {
        const filteredChildren = route.children.filter((child) => {
          // Keep public children
          if (!child.meta?.requiresAuth) {
            return true
          }

          // Super users see all children
          if (isSuperUser) {
            return true
          }

          // Check permissions for protected children
          if (child.meta?.permissions) {
            return userStore.hasAnyPermission(child.meta.permissions)
          }

          // If no specific permissions required, just need to be authenticated
          return isAuthenticated
        })

        // Always return the parent with filtered children
        return { ...route, children: filteredChildren }
      }

      return route
    })
    .filter(Boolean) as INavigationRoute[]
})

const bottomRouteNames = ['users', 'preferences']

const mainRoutes = computed(() =>
  filteredRoutes.value.filter(r => !bottomRouteNames.includes(r.name))
)

const bottomRoutes = computed(() =>
  filteredRoutes.value.filter(r => bottomRouteNames.includes(r.name))
)

// Navigation helpers
const isActiveRoute = (routeItem: INavigationRoute) => {
  return route.name === routeItem.name
}

const isActiveChildRoute = (child: INavigationRoute) => {
  // For PowerBI children, also check pageName since they share the same route name
  if (child.meta?.isPowerBI && child.meta?.pageName) {
    return route.name === child.name && route.params.pageName === child.meta.pageName
  }
  return route.name === child.name
}

const isParentActive = (parent: INavigationRoute) => {
  // For PowerBI parents, active if route name matches (children share the same route name)
  if (parent.meta?.isPowerBI && parent.children) {
    return route.name === parent.name
  }
  // Parent is active if it's the current route or has an active child
  if (isActiveRoute(parent)) {
    return true
  }
  if (parent.children) {
    return parent.children.some((child) => isActiveChildRoute(child))
  }
  return false
}

const toggleExpanded = (idx: number) => {
  expandedStates[idx] = !expandedStates[idx]
}

const iconColor = (routeItem: INavigationRoute) => {
  if (isActiveRoute(routeItem)) return 'primary'
  if (routeItem.children && routeItem.children.some((child) => isActiveChildRoute(child))) return 'primary'
  return 'secondary'
}

const textColor = (routeItem: INavigationRoute) => {
  if (isActiveRoute(routeItem)) return 'primary'
  if (routeItem.children && routeItem.children.some((child) => isActiveChildRoute(child))) return 'primary'
  return 'textPrimary'
}

// Initialize expanded states
const initializeExpandedStates = () => {
  filteredRoutes.value.forEach((navRoute, idx) => {
    if (navRoute.children && navRoute.children.length > 0) {
      // Check if not already set
      if (expandedStates[idx] === undefined) {
        // Auto-expand if it has an active child
        const hasActiveChild = navRoute.children.some((child) => isActiveChildRoute(child))
        // Auto-expand PowerBI parents when the route name matches
        const isPowerBIActive = navRoute.meta?.isPowerBI && route.name === navRoute.name
        expandedStates[idx] = hasActiveChild || !!isPowerBIActive
      }
    }
  })
}

// Handle navigation clicks
const handleRouteClick = (routeItem: INavigationRoute) => {
  // Special handling for PowerBI routes
  if (routeItem.meta.isPowerBI) {
    const codename = routeItem.meta.defaultCodename || 'report'
    const name = routeItem.meta.defaultName || 'Report'
    const params: Record<string, string> = { codename, name }

    // Include pageName if specified (for child page navigation)
    if (routeItem.meta.pageName) {
      params.pageName = routeItem.meta.pageName
    }

    router.push({
      name: routeItem.name,
      params,
    })
  } else {
    // Regular navigation
    router.push({ name: routeItem.name })
  }
}

// Handle entity change
const handleEntityChange = async (entityId: number) => {
  await userStore.switchEntity(entityId)
  // Refresh the current page to load new entity data
  router.go(0)
}

// Logout
const logout = async () => {
  try {
    userStore.clearUser()

    // MSAL logout
    await instance.logoutPopup({
      mainWindowRedirectUri: '/',
    })
    
    router.push({ name: 'login' })
  } catch (error) {
    console.error('Logout error:', error)
    router.push({ name: 'login' })
  }
}

const safeLogout = async () => {
  try {
    // Ensure MSAL is initialized
    if (!instance.getActiveAccount()) {
      const accounts = instance.getAllAccounts()
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0])
      }
    }

    await instance.logoutRedirect({
      postLogoutRedirectUri: '/login',
    })
  } catch (error) {
    console.error('MSAL logout failed:', error)
    router.replace({ name: 'login' })
  } finally {
    userStore.clearUser()
  }
}

// Watchers
watch(
  () => route.fullPath,
  () => {
    // Don't reset the expanded states on route change
    // Just update if there's a new active child or active PowerBI parent
    filteredRoutes.value.forEach((navRoute, idx) => {
      if (navRoute.children && navRoute.children.length > 0) {
        const hasActiveChild = navRoute.children.some((child) => isActiveChildRoute(child))
        const isPowerBIActive = navRoute.meta?.isPowerBI && route.name === navRoute.name
        if (hasActiveChild || isPowerBIActive) {
          expandedStates[idx] = true
        }
      }
    })
  },
)

watch(
  () => userStore.userPermissions,
  () => {
    initializeExpandedStates()
  },
  { deep: true },
)

watch(
  isAuthenticated,
  async (authenticated) => {
    console.log('isAuthenticated:', authenticated)

    if (authenticated) {
      // Fetch sidebar pages when user becomes authenticated
      const FIXED_ENTITY_ID = '10371'
      if (!sidebarStore.loaded) {
        await sidebarStore.fetchSidebarPages(FIXED_ENTITY_ID)
        initializeExpandedStates()
      }
    } else {
      sidebarStore.reset()
      await safeLogout()
    }
  },
  { immediate: true }
)

// Lifecycle
onMounted(async () => {
  // Fetch dynamic sidebar pages from API
  const FIXED_ENTITY_ID = '10371'
  if (userStore.isAuthenticated && !sidebarStore.loaded) {
    await sidebarStore.fetchSidebarPages(FIXED_ENTITY_ID)
  }
  initializeExpandedStates()
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  :deep(.va-sidebar__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .user-info-section {
    background: rgba(var(--va-primary), 0.05);
    border-radius: 8px;
  }

  .navigation-menu {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 15px 5px;
  }

  .parent-with-children {
    margin-bottom: 2px;
  }

  .sidebar-item {
    transition: all 0.2s ease;
    cursor: pointer;
    margin: 2px 8px;
    border-radius: 8px;

    &.parent-item {
      font-weight: 500;

      &.has-children {
        :deep(.va-sidebar-item__content) {
          position: relative;
          user-select: none;
        }
      }

      &.standalone {
        margin-bottom: 4px;
      }
    }

    &.child-item {
      margin: 2px 0;
      border-radius: 8px;

      :deep(.va-sidebar-item__content) {
        font-size: 0.675rem;
      }
    }

    

    &:hover {
      background: rgba(var(--va-primary), 0.05);
    }

    :deep(.va-sidebar-item__content) {
      display: flex;
      align-items: center;
    }
  }

  .va-sidebar-item-title {
    font-size: .805rem !important;
    margin-left: -10px;
  }

  .child-routes {
    background: rgba(var(--va-background-element), 0.3);
    border-left: 2px solid rgba(var(--va-primary), 0.15);
    
    margin-top: 2px;
    margin-bottom: 4px;
    padding: 4px 0;
    overflow: hidden;
  }

  .expand-icon {
    transition: transform 0.3s ease;

    &.rotate-180 {
      transform: rotate(180deg);
    }
  }

  // Smooth slide transition
  .slide-enter-active,
  .slide-leave-active {
    transition: all 0.3s ease;
  }

  .slide-enter-from {
    opacity: 0;
    transform: translateY(-10px);
  }

  .slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
